import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import SiteSelector, { useSiteSelector } from "../components/SiteSelector";
import {
  CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Line, LineChart, Legend
} from "recharts";

const PALETTE = ["#00E5FF", "#FFB300", "#00E676", "#FF3B30", "#B388FF", "#FF7043", "#26C6DA", "#EC407A"];

export default function Trends() {
  const { sites, siteId, setSiteId } = useSiteSelector();
  const [tags, setTags] = useState([]);
  const [selected, setSelected] = useState([]);
  const [mode, setMode] = useState("window");
  const [minutes, setMinutes] = useState(15);
  const initEnd = new Date();
  const initStart = new Date(Date.now() - 60 * 60 * 1000);
  const fmtLocal = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  const [startDt, setStartDt] = useState(fmtLocal(initStart));
  const [endDt, setEndDt] = useState(fmtLocal(initEnd));
  const [series, setSeries] = useState({});

  useEffect(() => {
    if (!siteId) return;
    api.get("/tags", { params: { site_id: siteId } }).then(({ data }) => {
      const analog = data.filter(t => t.tag_type === "analog");
      setTags(analog);
      setSelected(analog[0] ? [analog[0].id] : []);
    });
  }, [siteId]);

  useEffect(() => {
    if (!selected.length) { setSeries({}); return; }
    let mounted = true;
    const load = async () => {
      const params = mode === "window"
        ? { minutes }
        : { start: new Date(startDt).toISOString(), end: new Date(endDt).toISOString() };
      try {
        const all = await Promise.all(
          selected.map(id => api.get(`/tags/${id}/history`, { params }).then(r => [id, r.data]))
        );
        if (!mounted) return;
        const map = {};
        all.forEach(([id, d]) => { map[id] = d.readings; });
        setSeries(map);
      } catch {}
    };
    load();
    const iv = mode === "window" ? setInterval(load, 3000) : null;
    return () => { mounted = false; if (iv) clearInterval(iv); };
  }, [selected, mode, minutes, startDt, endDt]);

  // Merge series by timestamp
  const data = useMemo(() => {
    const map = new Map();
    for (const [id, arr] of Object.entries(series)) {
      for (const r of arr) {
        const k = r.ts;
        if (!map.has(k)) map.set(k, { ts: new Date(r.ts).toLocaleTimeString() });
        map.get(k)[id] = r.value;
      }
    }
    return Array.from(map.values()).sort((a, b) => a.ts.localeCompare(b.ts));
  }, [series]);

  const toggleTag = (id) => {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : (s.length >= 8 ? s : [...s, id]));
  };

  const tagById = Object.fromEntries(tags.map(t => [t.id, t]));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="scada-label">HISTORICAL ANALYSIS</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter">Trends</h1>
        </div>
        <SiteSelector sites={sites} siteId={siteId} setSiteId={setSiteId} />
      </div>

      <div className="grid grid-cols-12 gap-1">
        {/* Tag selector */}
        <div className="col-span-12 lg:col-span-3 scada-panel p-3 max-h-[420px] overflow-y-auto">
          <div className="scada-label mb-2">TAGS ({selected.length}/8)</div>
          {tags.map((t, i) => {
            const idx = selected.indexOf(t.id);
            return (
              <label key={t.id} className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-[#1E2226]/50 px-2" data-testid={`trend-toggle-${t.id}`}>
                <input type="checkbox" checked={idx >= 0} onChange={() => toggleTag(t.id)} className="!w-3 !h-3" />
                <span className="w-3 h-3 inline-block" style={{ background: idx >= 0 ? PALETTE[idx % 8] : "#2C3238" }} />
                <span className="font-mono-metric text-xs text-[#E3E8EE] truncate">{t.name}</span>
                <span className="font-mono-metric text-[10px] text-[#5A6573] ml-auto">{t.unit}</span>
              </label>
            );
          })}
        </div>

        {/* Chart + controls */}
        <div className="col-span-12 lg:col-span-9 space-y-3">
          <div className="scada-panel p-4 flex flex-wrap gap-4 items-end">
            <div className="flex gap-1" data-testid="trend-mode">
              {["window", "range"].map(m => (
                <button key={m} onClick={() => setMode(m)} data-testid={`mode-${m}`}
                  className={`px-3 py-2 border font-mono-metric text-xs uppercase tracking-wider ${mode === m ? "border-[#00E5FF] text-[#00E5FF]" : "border-[#2C3238] text-[#9BA4B5]"}`}>
                  {m === "window" ? "Live Window" : "Date Range"}
                </button>
              ))}
            </div>
            {mode === "window" ? (
              <div className="flex gap-1">
                {[5, 15, 60, 240, 720].map(m => (
                  <button key={m} onClick={() => setMinutes(m)} data-testid={`window-${m}`}
                    className={`px-3 py-2 border font-mono-metric text-xs ${minutes === m ? "border-[#00E5FF] text-[#00E5FF]" : "border-[#2C3238] text-[#9BA4B5]"}`}>
                    {m < 60 ? `${m}m` : `${m / 60}h`}
                  </button>
                ))}
              </div>
            ) : (
              <>
                <div>
                  <div className="scada-label mb-1">START</div>
                  <input type="datetime-local" value={startDt} onChange={(e) => setStartDt(e.target.value)} data-testid="trend-start" className="border px-3 py-2 text-xs font-mono-metric" />
                </div>
                <div>
                  <div className="scada-label mb-1">END</div>
                  <input type="datetime-local" value={endDt} onChange={(e) => setEndDt(e.target.value)} data-testid="trend-end" className="border px-3 py-2 text-xs font-mono-metric" />
                </div>
              </>
            )}
            <div className="ml-auto font-mono-metric text-[10px] text-[#5A6573]">{data.length} SAMPLES</div>
          </div>

          <div className="scada-panel p-4">
            <div className="h-[420px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data}>
                  <CartesianGrid stroke="#2C3238" strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="ts" stroke="#5A6573" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} interval="preserveStartEnd" minTickGap={50} />
                  <YAxis stroke="#5A6573" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} domain={["auto", "auto"]} />
                  <Tooltip 
                    contentStyle={{ 
                      background: "rgba(20, 23, 26, 0.85)", 
                      backdropFilter: "blur(12px)",
                      border: "1px solid rgba(0, 229, 255, 0.3)", 
                      borderRadius: 0, 
                      fontFamily: "IBM Plex Mono", 
                      fontSize: 11,
                      boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.5)"
                    }}
                    itemStyle={{ color: "#E3E8EE" }}
                    labelStyle={{ color: "#00E5FF", fontWeight: "bold" }}
                    formatter={(v, key) => [v, tagById[key]?.name || key]} 
                  />
                  <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 10 }}
                    formatter={(value) => tagById[value]?.name || value} />
                  {selected.map((id, i) => (
                    <Line key={id} type="monotone" dataKey={id} stroke={PALETTE[i % 8]} strokeWidth={2.5} dot={false} isAnimationActive={false} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
